import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  message,
  Upload,
  Button,
  Modal,
  TreeSelect,
  Modal as modal,
  FileUpload,
} from "@gw/web-basic-components";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { flat2TreeWrapper } from "@gw/gw-utils";
import { useBusinessConfig } from "../../../utils/business-config";
import type { ILeadingModalProps, ModalProps } from "./dto";
import styles from "./index.module.less";

/**
 * Modal 的默认参数
 */
const DEFAULT_MODAL_PROPS: ModalProps = {
  cancelText: "取消",
  okText: "确定",
};

const RESPONSE_OK = "00000";
const TEMPLATE_DOWNLOAD_PRE = "/api/sniper/file/";
const TEMPLATE_UPLOAD = "/api/sniper/file";

/**
 * 批量上传组件
 * @param props
 * @returns
 */
const LeadingIn = (props: ILeadingModalProps) => {
  const { apis, useRequest, fileRequest } = useBusinessConfig();

  /**
   * props 解构
   */
  const {
    title = "批量导入",
    apiName = "thingInstance",
    downloadType = "normal",
    downloadTitle = "下载模板表格",
    downloadButtonText = "下载空的模板表格",
    uploadType = "string",
    downloadName,
    visible,
    onCancel,
    onOk,
  } = props;

  /**
   * 文件上传
   */
  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * 获取物模板树，扁平数据
   * TODO: 这里是 hard code，后期需要处理
   */
  const [templateTreeData, fetchTemplateTree] = useRequest(
    apis.thingTemplate?.tree
  );

  /**
   * 获取下载模板的名称参数
   * 这里的类型只能用 any，上帝会原谅我的 O(∩_∩)O哈哈~
   */
  const [downloadParams, setDownloadParams] = useState<any>({});

  /**
   * 上传文件的文件名
   */
  const [uploadFileName, setUploadFileName] = useState("");

  /**
   * 要上传的文件
   */
  const [uploadFile, setUploadFile] = useState<File>(null);

  /**
   * 获取批量导入的模板名称
   * 这里将会成为动态的，所以接口的名称要统一
   */
  const [downloadFileName, fetchDownloadFileName] = useRequest(
    apis[apiName]?.downloadTemplate
  );

  /**
   * 批量导入
   */
  const [{ loading: importLoading }, fetchUploadFile] = useRequest(
    apis[apiName]?.importTemplate
  );

  /**
   * 物模板树，树形数据
   */
  const treeData = useMemo(() => {
    if (!templateTreeData.data) {
      return [];
    }
    return flat2TreeWrapper(
      templateTreeData.data.dataList?.map((m) => ({
        ...m,
        key: m.code,
        value: m.code,
      })),
      {
        key: "code",
        id: "code",
        title: "name",
        parentId: "parentCode",
      }
    );
  }, [templateTreeData.data]);

  /**
   * 下载模板
   */
  const downLoadHandler = useCallback(() => {
    if (downloadType === "tree") {
      if (!downloadParams?.codes?.length) {
        message.error("请选择物模板");
        return;
      }
      fetchDownloadFileName(downloadParams);
    }
    // 最新的下载都是固定的名称
    if (uploadType === "stream") {
      if (!downloadName) {
        message.error("缺少下载模板！");
        return;
      }
      window.location.href = `${TEMPLATE_DOWNLOAD_PRE}${downloadName}`;
    }
  }, [downloadParams, downloadName, uploadType]);

  /**
   * 修改下载项
   */
  const onTreeSelectChange = useCallback((params: any) => {
    setDownloadParams({
      codes: params,
    });
  }, []);

  /**
   * 文件选择发生变化
   */
  // const onFileInputChange = useCallback(
  //   (event: React.ChangeEvent<HTMLInputElement>) => {
  //     const file = event.target.files?.[0];
  //     if (file) {
  //       setUploadFile(file);
  //     }
  //     event.target.value = "";
  //   },
  //   [apiName]
  // );
  const onFileInputChange = useCallback(
    (info: any) => {
      const file = info.fileList[0];
      if (file) {
        setUploadFile(file);
      }
      // event.target.value = "";
    },
    [apiName]
  );

  /**
   * 文件选择发生变化
   */
  const handleFileChange = (info: any) => {
    console.log("handleFileChange", info);
    if (uploadType === "string") {
      if (info?.file?.status !== "uploading") {
        if (info?.file?.response?.code === RESPONSE_OK) {
          if (info?.file?.status === "removed") {
            setUploadFileName("");
          } else {
            setUploadFileName(info?.file?.response?.data?.fileName);
          }
        } else {
          message.error(info?.file?.response?.errorMsg || "上传失败");
        }
      }
    } else if (uploadType === "stream") {
      onFileInputChange(info);
    }
  };

  /**
   * 下一步
   */
  const onNextStep: ModalProps["onOk"] = () => {
    // 根据文件名导入
    if (uploadType === "string") {
      fetchUploadFile({
        fileName: uploadFileName,
      })
        .then(() => {
          modal.success({
            content: "导入成功",
          });
          onOk();
        })
        .catch(() => {
          // 要不咱就不弹那个框了吧
        });
    }
    // 数据流上传导入
    if (uploadType === "stream") {
      // 校验文件
      if (!uploadFile) {
        message.error("请选择要导入的文件");
        return;
      }
      // 校验配置
      if (!props.uploadApi) {
        message.error("未配置导入地址");
        return;
      }
      fileRequest(uploadFile, props.uploadApi)
        .then((res: IResponse<any>) => {
          if (res.code === RESPONSE_OK) {
            modal.success({
              content: "导入成功",
            });
            onOk();
          } else {
            message.error(res.errorMsg);
          }
        })
        .catch((e) => message.error(e.message || e.Message || "导入失败"));
    }
  };

  const downLoadFile = () => {};
  const doDelete = () => {};
  const handlePreview = () => {};
  const doCancel = () => {};

  /**
   * 调用数据
   */
  useEffect(() => {
    if (visible) {
      fetchTemplateTree({ size: -1 });
      setUploadFile(null);
    }
  }, [visible]);

  /**
   * 下载模板
   */
  useEffect(() => {
    if (!downloadFileName.data) return;
    window.location.href = `${TEMPLATE_DOWNLOAD_PRE}${downloadFileName.data}`;
  }, [downloadFileName.data]);

  return (
    <Modal
      {...DEFAULT_MODAL_PROPS}
      title={title}
      visible={visible}
      onOk={onNextStep}
      onCancel={onCancel}
      okButtonProps={{ loading: importLoading }}
    >
      <div className={styles.content}>
        {/* 物模板树下载 */}
        {downloadType === "tree" && (
          <>
            <div className={styles.label}>
              <span>1、选择物模板</span>
              <a
                className={styles.download}
                href="javascript:void(0)"
                onClick={downLoadHandler}
              >
                <DownloadOutlined style={{ color: "#8d8d8d" }} />
                下载
              </a>
            </div>
            <div className={styles.treeWrapper}>
              <TreeSelect
                className={styles.tree}
                multiple={true}
                style={{ width: "100%" }}
                placeholder="请选择物模板"
                treeData={treeData as any}
                onChange={onTreeSelectChange}
              />
            </div>
          </>
        )}
        {/* 普通模板下载 */}
        {downloadType === "normal" && (
          <>
            <div className={styles.label}>
              <span>1、{downloadTitle}</span>
            </div>
            <Button
              size="large"
              type="primary"
              className={styles.btnDownload}
              onClick={downLoadHandler}
            >
              {downloadButtonText}
            </Button>
          </>
        )}
        <div className={styles.label}>
          <span>2、上传文件</span>
        </div>
        <div className={styles.uploadWrapper}>
          <FileUpload
            action={TEMPLATE_UPLOAD}
            multiple={false}
            maxCount={1}
            accept=".xlsx"
            showUploadList={true}
            onChange={handleFileChange}
            onDownload={downLoadFile}
            onRemove={doDelete}
            onPreview={handlePreview}
            cancelUpload={doCancel}
            uploadText={
              <div>
                <p className={styles.tips}>支持格式</p>
                <p className={styles.tips}>
                  jpg .png .doc .docx .pdf .xls .xlsx
                </p>
                <p className={styles.tips}>单个文件不能超过10MB</p>
              </div>
            }
          />
          {/* {uploadType === "string" && (
          <FileUpload
            action={TEMPLATE_UPLOAD}
            multiple={false}
            maxCount={1}
            accept=".xlsx"
            showUploadList={true}
            onChange={handleFileChange}
            onDownload={downLoadFile}
            onRemove={doDelete}
            onPreview={handlePreview}
            cancelUpload={doCancel}
            uploadText={
              <div>
                <p className={styles.tips}>支持格式</p>
                <p className={styles.tips}>
                  jpg .png .doc .docx .pdf .xls .xlsx
                </p>
                <p className={styles.tips}>单个文件不能超过10MB</p>
              </div>
            }
          />
        )}
        {uploadType === "stream" && (
          <>
            <Button
              icon={<UploadOutlined />}
              style={{ width: "100%", flex: 1 }}
              onClick={() => fileInput.current?.click()}
            >
              {uploadFile ? (
                uploadFile.name
              ) : (
                <>
                  点击上传
                  <span className={styles.uploadBtnDes}>
                    （支持扩展名：.xlsx）
                  </span>
                </>
              )}
            </Button>
            <input
              onChange={onFileInputChange}
              className={styles.fileInput}
              ref={fileInput}
              type="file"
              accept=".xlsx"
            />
          </>
        )} */}
        </div>
      </div>
    </Modal>
  );
};

export default LeadingIn;
